import {
  Position,
  type Edge as ReactFlowEdge,
  type Node as ReactFlowNode,
} from "@xyflow/react";
import {
  FlowchartDirection,
  FlowchartLabelOrigin,
  FlowchartNodeRole,
  FlowchartNodeShape,
  type FlowchartEdge,
  type FlowchartNode,
  type FlowchartSubgraph,
} from "./parse-mermaid-flowchart";
import type { ResolvedFlowchart } from "./resolve-flowchart";
import type { FlowchartEdgeData, FlowchartNodeData } from "./flowchart-types";
import {
  DEFAULT_ACCENT_BY_SHAPE,
  PARALLEL_EDGE_GAP,
  SELF_LOOP_RADIUS,
  SHAPE_ICON,
} from "./flowchart-constants";
import type { NodeBox } from "./flowchart-layout-types";
import {
  getNodeLabel,
  getNodeShape,
  getNodeSize,
  getSubgraphDepth,
  getSubgraphSeedSize,
} from "./flowchart-layout-sizing";
import {
  getDefaultNodePresentation,
  getDefaultSubgraphPresentation,
  resolvePresentation,
  type ResolvedPresentation,
} from "./flowchart-layout-presentation";

const REACT_FLOW_PARENT_EXTENT = "parent";
const FLOWCHART_CARD_NODE_TYPE = "flowchartCard";
const FLOWCHART_GROUP_NODE_TYPE = "flowchartGroup";
const FLOWCHART_EDGE_TYPE = "flowchartDashed";

const SOURCE_POSITION_BY_DIRECTION: Record<FlowchartDirection, Position> = {
  [FlowchartDirection.TopToBottom]: Position.Bottom,
  [FlowchartDirection.BottomToTop]: Position.Top,
  [FlowchartDirection.LeftToRight]: Position.Right,
  [FlowchartDirection.RightToLeft]: Position.Left,
};

const TARGET_POSITION_BY_DIRECTION: Record<FlowchartDirection, Position> = {
  [FlowchartDirection.TopToBottom]: Position.Top,
  [FlowchartDirection.BottomToTop]: Position.Bottom,
  [FlowchartDirection.LeftToRight]: Position.Left,
  [FlowchartDirection.RightToLeft]: Position.Right,
};

enum NodeDegreeField {
  InDegree = "inDegree",
  OutDegree = "outDegree",
}

type NodeDegree = Record<NodeDegreeField, number>;

interface ParallelEdgeInfo {
  parallelIndex: number;
  parallelCount: number;
  parallelOffset: number;
}

interface PositionedNodeInput {
  parentId: string | null;
  box: NodeBox;
  parentBox: NodeBox | null;
}

enum TerminatorRoleCandidate {
  Start = "start",
  End = "end",
}

const TERMINATOR_ROLE_BY_CANDIDATE: Record<
  TerminatorRoleCandidate,
  FlowchartNodeRole
> = {
  [TerminatorRoleCandidate.Start]: FlowchartNodeRole.Start,
  [TerminatorRoleCandidate.End]: FlowchartNodeRole.End,
};

const TERMINATOR_ROLE_ORDER: TerminatorRoleCandidate[] = [
  TerminatorRoleCandidate.Start,
  TerminatorRoleCandidate.End,
];

const TERMINATOR_ROLE_MATCHER_BY_CANDIDATE: Record<
  TerminatorRoleCandidate,
  (degree: NodeDegree | undefined) => boolean
> = {
  [TerminatorRoleCandidate.Start]: (degree) =>
    (degree?.[NodeDegreeField.InDegree] ?? 0) === 0,
  [TerminatorRoleCandidate.End]: (degree) =>
    (degree?.[NodeDegreeField.OutDegree] ?? 0) === 0,
};

export function getSourcePosition(direction: FlowchartDirection): Position {
  return SOURCE_POSITION_BY_DIRECTION[direction];
}

export function getTargetPosition(direction: FlowchartDirection): Position {
  return TARGET_POSITION_BY_DIRECTION[direction];
}

function incrementDegree(input: {
  degreeMap: Map<string, NodeDegree>;
  nodeIds: ReadonlySet<string>;
  nodeId: string;
  field: NodeDegreeField;
}): void {
  const degree = input.degreeMap.get(input.nodeId);
  if (!degree || !input.nodeIds.has(input.nodeId)) {
    return;
  }
  input.degreeMap.set(input.nodeId, {
    ...degree,
    [input.field]: degree[input.field] + 1,
  });
}

export function buildDegreeMap(input: {
  nodes: readonly FlowchartNode[];
  edges: readonly FlowchartEdge[];
}): Map<string, NodeDegree> {
  const nodeIds = new Set(input.nodes.map((node) => node.id));
  const seedEntries = input.nodes.map<[string, NodeDegree]>((node) => [
    node.id,
    { [NodeDegreeField.InDegree]: 0, [NodeDegreeField.OutDegree]: 0 },
  ]);
  const degreeMap = new Map(seedEntries);

  input.edges.forEach((edge) => {
    incrementDegree({
      degreeMap,
      nodeIds,
      nodeId: edge.from,
      field: NodeDegreeField.OutDegree,
    });
    incrementDegree({
      degreeMap,
      nodeIds,
      nodeId: edge.to,
      field: NodeDegreeField.InDegree,
    });
  });

  return degreeMap;
}

function getTerminatorRole(degree: NodeDegree | undefined): FlowchartNodeRole {
  const candidate = TERMINATOR_ROLE_ORDER.find((roleCandidate) =>
    TERMINATOR_ROLE_MATCHER_BY_CANDIDATE[roleCandidate](degree),
  );
  if (!candidate) {
    return FlowchartNodeRole.None;
  }
  return TERMINATOR_ROLE_BY_CANDIDATE[candidate];
}

function deriveRole(input: {
  node: FlowchartNode;
  degree: NodeDegree | undefined;
}): FlowchartNodeRole {
  if (getNodeShape(input.node) !== FlowchartNodeShape.Terminator) {
    return FlowchartNodeRole.None;
  }
  return getTerminatorRole(input.degree);
}

function getPairKey(edge: FlowchartEdge): string {
  const [first, second] = [edge.from, edge.to].sort();
  return `${first}\u0000${second}`;
}

export function getParallelGroups(
  edges: readonly FlowchartEdge[],
): Map<string, string[]> {
  return edges.reduce<Map<string, string[]>>((groups, edge) => {
    const key = getPairKey(edge);
    const edgeIds = groups.get(key) ?? [];
    groups.set(key, [...edgeIds, edge.id]);
    return groups;
  }, new Map());
}

function getParallelOffset(input: {
  parallelIndex: number;
  parallelCount: number;
}): number {
  if (input.parallelCount <= 1) {
    return 0;
  }
  const midpoint = (input.parallelCount - 1) / 2;
  return (input.parallelIndex - midpoint) * PARALLEL_EDGE_GAP;
}

function getParallelEdgeInfo(input: {
  edge: FlowchartEdge;
  parallelGroups: ReadonlyMap<string, readonly string[]>;
}): ParallelEdgeInfo {
  const group = input.parallelGroups.get(getPairKey(input.edge)) ?? [
    input.edge.id,
  ];
  const parallelIndex = Math.max(0, group.indexOf(input.edge.id));
  const parallelCount = group.length;
  return {
    parallelIndex,
    parallelCount,
    parallelOffset: getParallelOffset({ parallelIndex, parallelCount }),
  };
}

function getPosition(input: PositionedNodeInput): { x: number; y: number } {
  if (!input.parentId || !input.parentBox) {
    return { x: input.box.x, y: input.box.y };
  }
  return {
    x: input.box.x - input.parentBox.x,
    y: input.box.y - input.parentBox.y,
  };
}

function getReactFlowParentId(parentId: string | null): string | undefined {
  if (parentId == null) {
    return undefined;
  }
  return parentId;
}

function getReactFlowExtent(
  parentId: string | null,
): typeof REACT_FLOW_PARENT_EXTENT | undefined {
  if (parentId == null) {
    return undefined;
  }
  return REACT_FLOW_PARENT_EXTENT;
}

function getParentBox(input: {
  parentId: string | null;
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
}): NodeBox | null {
  if (input.parentId == null) {
    return null;
  }
  return input.nodeBoxesById.get(input.parentId) ?? null;
}

function getGroupNodeBox(input: {
  subgraph: FlowchartSubgraph;
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
}): NodeBox {
  const box = input.nodeBoxesById.get(input.subgraph.id);
  if (box != null) {
    return box;
  }
  return {
    ...getSubgraphSeedSize(input.subgraph),
    x: 0,
    y: 0,
  };
}

function getSubgraphPresentation(input: {
  subgraph: FlowchartSubgraph;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
}): ResolvedPresentation {
  const presentation = input.presentationById.get(input.subgraph.id);
  if (presentation != null) {
    return presentation;
  }
  return getDefaultSubgraphPresentation();
}

function getSubgraphLabel(subgraph: FlowchartSubgraph): string {
  return subgraph.label ?? subgraph.id;
}

function getSubgraphLabelOrigin(
  subgraph: FlowchartSubgraph,
): FlowchartLabelOrigin {
  if (subgraph.label) {
    return FlowchartLabelOrigin.ExplicitLabel;
  }
  return FlowchartLabelOrigin.BareDeclaration;
}

function buildGroupNodeData(input: {
  subgraph: FlowchartSubgraph;
  box: NodeBox;
  presentation: ResolvedPresentation;
}): FlowchartNodeData {
  return {
    label: getSubgraphLabel(input.subgraph),
    nodeId: input.subgraph.id,
    shape: FlowchartNodeShape.Process,
    role: FlowchartNodeRole.None,
    isSubgraph: true,
    labelOrigin: getSubgraphLabelOrigin(input.subgraph),
    resolvedStyle: input.presentation.resolvedStyle,
    accentColor: input.presentation.accentColor,
    nodeWidth: input.box.width,
    nodeHeight: input.box.height,
    icon: null,
  };
}

function buildGroupNode(input: {
  subgraph: FlowchartSubgraph;
  subgraphById: ReadonlyMap<string, FlowchartSubgraph>;
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
}): ReactFlowNode<FlowchartNodeData> {
  const box = getGroupNodeBox({
    subgraph: input.subgraph,
    nodeBoxesById: input.nodeBoxesById,
  });
  const parentBox = getParentBox({
    parentId: input.subgraph.parent,
    nodeBoxesById: input.nodeBoxesById,
  });
  const presentation = getSubgraphPresentation({
    subgraph: input.subgraph,
    presentationById: input.presentationById,
  });
  const position = getPosition({
    parentId: input.subgraph.parent,
    box,
    parentBox,
  });
  const depth = getSubgraphDepth({
    subgraph: input.subgraph,
    subgraphById: input.subgraphById,
  });
  return {
    id: input.subgraph.id,
    type: FLOWCHART_GROUP_NODE_TYPE,
    position,
    width: box.width,
    height: box.height,
    parentId: getReactFlowParentId(input.subgraph.parent),
    extent: getReactFlowExtent(input.subgraph.parent),
    zIndex: depth,
    data: buildGroupNodeData({ subgraph: input.subgraph, box, presentation }),
    style: { width: box.width, height: box.height },
  };
}

export function buildGroupNodes(input: {
  orderedSubgraphs: readonly FlowchartSubgraph[];
  subgraphById: ReadonlyMap<string, FlowchartSubgraph>;
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
}): ReactFlowNode<FlowchartNodeData>[] {
  return input.orderedSubgraphs.map((subgraph) =>
    buildGroupNode({
      subgraph,
      subgraphById: input.subgraphById,
      nodeBoxesById: input.nodeBoxesById,
      presentationById: input.presentationById,
    }),
  );
}

function getChildNodeBox(input: {
  node: FlowchartNode;
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
}): NodeBox {
  const box = input.nodeBoxesById.get(input.node.id);
  if (box != null) {
    return box;
  }
  return { ...getNodeSize(input.node), x: 0, y: 0 };
}

function getNodePresentation(input: {
  node: FlowchartNode;
  shape: FlowchartNodeShape;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
}): ResolvedPresentation {
  const presentation = input.presentationById.get(input.node.id);
  if (presentation != null) {
    return presentation;
  }
  return getDefaultNodePresentation(input.shape);
}

function buildChildNodeData(input: {
  node: FlowchartNode;
  shape: FlowchartNodeShape;
  role: FlowchartNodeRole;
  box: NodeBox;
  presentation: ResolvedPresentation;
}): FlowchartNodeData {
  return {
    label: getNodeLabel(input.node),
    nodeId: input.node.id,
    shape: input.shape,
    role: input.role,
    isSubgraph: false,
    labelOrigin: input.node.labelOrigin,
    resolvedStyle: input.presentation.resolvedStyle,
    accentColor: input.presentation.accentColor,
    nodeWidth: input.box.width,
    nodeHeight: input.box.height,
    icon: SHAPE_ICON[input.shape],
  };
}

function buildChildNode(input: {
  node: FlowchartNode;
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
  degreeMap: ReadonlyMap<string, NodeDegree>;
  sourcePosition: Position;
  targetPosition: Position;
}): ReactFlowNode<FlowchartNodeData> {
  const shape = getNodeShape(input.node);
  const box = getChildNodeBox({
    node: input.node,
    nodeBoxesById: input.nodeBoxesById,
  });
  const parentBox = getParentBox({
    parentId: input.node.parent,
    nodeBoxesById: input.nodeBoxesById,
  });
  const presentation = getNodePresentation({
    node: input.node,
    shape,
    presentationById: input.presentationById,
  });
  const role = deriveRole({
    node: input.node,
    degree: input.degreeMap.get(input.node.id),
  });
  const position = getPosition({ parentId: input.node.parent, box, parentBox });
  return {
    id: input.node.id,
    type: FLOWCHART_CARD_NODE_TYPE,
    position,
    width: box.width,
    height: box.height,
    parentId: getReactFlowParentId(input.node.parent),
    extent: getReactFlowExtent(input.node.parent),
    zIndex: 100,
    sourcePosition: input.sourcePosition,
    targetPosition: input.targetPosition,
    data: buildChildNodeData({
      node: input.node,
      shape,
      role,
      box,
      presentation,
    }),
    style: { width: box.width, height: box.height },
  };
}

export function buildChildNodes(input: {
  nodes: readonly FlowchartNode[];
  nodeBoxesById: ReadonlyMap<string, NodeBox>;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
  degreeMap: ReadonlyMap<string, NodeDegree>;
  sourcePosition: Position;
  targetPosition: Position;
}): ReactFlowNode<FlowchartNodeData>[] {
  return input.nodes.map((node) =>
    buildChildNode({
      node,
      nodeBoxesById: input.nodeBoxesById,
      presentationById: input.presentationById,
      degreeMap: input.degreeMap,
      sourcePosition: input.sourcePosition,
      targetPosition: input.targetPosition,
    }),
  );
}

function buildEdge(input: {
  edge: FlowchartEdge;
  resolved: ResolvedFlowchart;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
  parallelGroups: ReadonlyMap<string, readonly string[]>;
}): ReactFlowEdge<FlowchartEdgeData> {
  const sourcePresentation = input.presentationById.get(input.edge.from);
  const classPresentation = resolvePresentation({
    classNames: input.edge.classNames,
    inlineStyle: null,
    classDefs: input.resolved.classDefs,
    defaultAccent:
      sourcePresentation?.accentColor ??
      DEFAULT_ACCENT_BY_SHAPE[FlowchartNodeShape.Process],
    includeDefaultClass: false,
  });
  const parallelInfo = getParallelEdgeInfo({
    edge: input.edge,
    parallelGroups: input.parallelGroups,
  });
  return {
    id: input.edge.id,
    source: input.edge.from,
    target: input.edge.to,
    type: FLOWCHART_EDGE_TYPE,
    data: {
      edgeLabel: input.edge.label,
      kind: input.edge.kind,
      startMarker: input.edge.startMarker,
      endMarker: input.edge.endMarker,
      parallelIndex: parallelInfo.parallelIndex,
      parallelCount: parallelInfo.parallelCount,
      accentColor: classPresentation.accentColor,
      selfLoopRadius: input.edge.from === input.edge.to ? SELF_LOOP_RADIUS : 0,
      parallelOffset: parallelInfo.parallelOffset,
    },
  };
}

export function buildEdges(input: {
  resolved: ResolvedFlowchart;
  presentationById: ReadonlyMap<string, ResolvedPresentation>;
  parallelGroups: ReadonlyMap<string, readonly string[]>;
}): ReactFlowEdge<FlowchartEdgeData>[] {
  return input.resolved.edges.map((edge) =>
    buildEdge({
      edge,
      resolved: input.resolved,
      presentationById: input.presentationById,
      parallelGroups: input.parallelGroups,
    }),
  );
}

export function getStartNodeId(
  childNodes: readonly ReactFlowNode<FlowchartNodeData>[],
): string | null {
  const firstStartNode = childNodes.find(
    (node) => node.data.role === FlowchartNodeRole.Start,
  );
  if (firstStartNode != null) {
    return firstStartNode.id;
  }
  const firstNode = childNodes[0];
  if (firstNode != null) {
    return firstNode.id;
  }
  return null;
}
